/**
 * Camera maths for the lights canvas, pure. A camera is {x, y, scale}: the world point at the canvas centre and the
 * world-to-CSS-pixel scale. Viewport is {width, height} in CSS pixels.
 */
import { CAMERA } from "./lightsConstants";

export const toScreen = (cam, view, x, y) => [(x - cam.x) * cam.scale + view.width / 2, (y - cam.y) * cam.scale + view.height / 2];
export const toWorld = (cam, view, sx, sy) => [(sx - view.width / 2) / cam.scale + cam.x, (sy - view.height / 2) / cam.scale + cam.y];

/** Camera that shows `box` ({minX,minY,maxX,maxY}) with FIT_PADDING around it, spans no smaller than minSpan. */
export function fitBox(box, view, minSpan = 0) {
  const w = Math.max(box.maxX - box.minX, minSpan, 1);
  const h = Math.max(box.maxY - box.minY, minSpan, 1);
  const pad = 1 + 2 * CAMERA.FIT_PADDING;
  const scale = Math.min(view.width / (w * pad), view.height / (h * pad));
  return { x: (box.minX + box.maxX) / 2, y: (box.minY + box.maxY) / 2, scale: Number.isFinite(scale) && scale > 0 ? scale : 1 };
}

/** Bounding box of the given node ids, null when none has a position. */
export function boxOf(ids, positions) {
  let box = null;
  ids.forEach((id) => {
    const p = positions.get(id);
    if (!p) return;
    if (!box) box = { minX: p.x, minY: p.y, maxX: p.x, maxY: p.y };
    else {
      box.minX = Math.min(box.minX, p.x); box.minY = Math.min(box.minY, p.y);
      box.maxX = Math.max(box.maxX, p.x); box.maxY = Math.max(box.maxY, p.y);
    }
  });
  return box;
}

/** The auto camera: whole category when nothing is in focus, else the focus box, never past MAX_SCALE_OVER_FIT. */
export function targetCamera(layout, focus, view) {
  const whole = fitBox(layout.bounds, view);
  const box = focus.length ? boxOf(focus, layout.positions) : null;
  if (!box) return whole;
  const cam = fitBox(box, view, CAMERA.MIN_FOCUS_SPAN);
  return { ...cam, scale: Math.min(Math.max(cam.scale, whole.scale), whole.scale * CAMERA.MAX_SCALE_OVER_FIT) };
}

export function clampScale(scale, fitScale) {
  return Math.min(Math.max(scale, fitScale * CAMERA.MIN_SCALE_UNDER_FIT), fitScale * CAMERA.MAX_SCALE_OVER_FIT);
}

/** One frame of approach toward `target`; `t` is the lerp factor (1 = jump). Returns [camera, settled]. */
export function stepCamera(cam, target, t) {
  const next = {
    x: cam.x + (target.x - cam.x) * t,
    y: cam.y + (target.y - cam.y) * t,
    scale: Math.exp(Math.log(cam.scale) + (Math.log(target.scale) - Math.log(cam.scale)) * t),
  };
  const settled = Math.abs(target.scale / next.scale - 1) < CAMERA.SETTLE_EPS
    && Math.hypot(target.x - next.x, target.y - next.y) * next.scale < 0.5;
  return [settled ? { ...target } : next, settled];
}

/** Zoom by `factor` keeping the world point under screen (sx, sy) fixed. */
export function zoomAt(cam, view, factor, sx, sy, fitScale) {
  const [wx, wy] = toWorld(cam, view, sx, sy);
  const scale = clampScale(cam.scale * factor, fitScale);
  return { scale, x: wx - (sx - view.width / 2) / scale, y: wy - (sy - view.height / 2) / scale };
}

/** Rajvis' wheel curve: factor = exp(-sign(dy) * max(min step, gain * |dy|)). */
export const wheelFactor = (dy) => Math.exp(-Math.sign(dy) * Math.max(CAMERA.WHEEL_MIN_STEP, CAMERA.WHEEL_GAIN * Math.abs(dy)));

export const panBy = (cam, dxPx, dyPx) => ({ ...cam, x: cam.x - dxPx / cam.scale, y: cam.y - dyPx / cam.scale });
